import {memo, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {useFrame} from '@react-three/fiber'
import * as THREE from 'three'
import type {SceneSprite3D} from '../../types/spriteTypes'

interface Sprite3DMeshV2Props {
  sprite: SceneSprite3D
}

interface LoadedTexture {
  url: string
  texture: THREE.Texture
}

const Sprite3DMeshV2 = memo(({sprite}: Sprite3DMeshV2Props) => {
  const [loaded, setLoaded] = useState<LoadedTexture | null>(null)
  const meshRef = useRef<THREE.Mesh>(null)

  // Link both shadow fields to `!unlit` — under the project's VSM shadow mode
  // (`shadows="variance"` on `<Canvas>`), three.js's WebGLShadowMap includes
  // ANY mesh with `receiveShadow=true` in the shadow render regardless of
  // `castShadow`, because VSM needs receiver depth in the shadow map. So we
  // must tie both fields together. The single `unlit` checkbox in the panel
  // controls "participates in lighting + shadows": when on → MeshBasicMaterial
  // + no cast + no receive. When off → MeshPhysicalMaterial + cast + receive.
  const lit = !sprite.unlit
  useLayoutEffect(() => {
    const mesh = meshRef.current
    if (!mesh) return
    mesh.castShadow = lit
    mesh.receiveShadow = lit
  }, [lit, loaded])

  useFrame(() => {
    const mesh = meshRef.current
    if (!mesh) return
    mesh.castShadow = lit
    mesh.receiveShadow = lit
  })

  useEffect(() => {
    const url = sprite.imageUrl
    if (!url) return
    const loader = new THREE.TextureLoader()
    loader.setCrossOrigin('anonymous')
    let disposed = false
    let tex: THREE.Texture | null = null
    loader.load(
      url,
      (texture) => {
        if (disposed) {
          texture.dispose()
          return
        }
        texture.colorSpace = THREE.SRGBColorSpace
        texture.anisotropy = 8
        tex = texture
        setLoaded({url, texture})
      },
      undefined,
      () => {
        // Swallow load errors — sprite will simply not render until the URL resolves.
      },
    )
    return () => {
      disposed = true
      if (tex) tex.dispose()
    }
  }, [sprite.imageUrl])

  const rotation = useMemo(
    (): [number, number, number] => [
      THREE.MathUtils.degToRad(sprite.rotationX),
      THREE.MathUtils.degToRad(sprite.rotationY),
      THREE.MathUtils.degToRad(sprite.rotationZ),
    ],
    [sprite.rotationX, sprite.rotationY, sprite.rotationZ],
  )

  const [planeWidth, planeHeight] = useMemo(() => {
    const aspect = sprite.imageAspect > 0 ? sprite.imageAspect : 1
    return aspect >= 1
      ? ([sprite.scale, sprite.scale / aspect] as const)
      : ([sprite.scale * aspect, sprite.scale] as const)
  }, [sprite.scale, sprite.imageAspect])

  // Only render once the loaded texture matches the current sprite URL — covers both
  // the empty-URL case and the in-flight URL-change case without a synchronous reset.
  if (!loaded || loaded.url !== sprite.imageUrl) return null

  return (
    <group position={[sprite.x, sprite.y, sprite.z]} rotation={rotation}>
      {/* `key` forces a fresh mesh instance every time the unlit checkbox
          toggles — the imperative write in useLayoutEffect / useFrame *should*
          be sufficient, but in practice it wasn't reliably taking effect on
          its own, so we belt-and-suspender it with a remount that's guaranteed
          to read the current value at construction time. */}
      <mesh key={sprite.unlit ? 'unlit' : 'lit'} ref={meshRef} castShadow={lit}>
        <planeGeometry args={[planeWidth, planeHeight]} />
        {sprite.unlit ? (
          <meshBasicMaterial map={loaded.texture} transparent alphaTest={0.01} side={THREE.DoubleSide} />
        ) : (
          <meshPhysicalMaterial
            map={loaded.texture}
            transparent
            alphaTest={0.01}
            side={THREE.DoubleSide}
            metalness={sprite.material.metalness}
            roughness={sprite.material.roughness}
            clearcoat={sprite.material.clearcoat}
            clearcoatRoughness={sprite.material.clearcoatRoughness}
            reflectivity={sprite.material.reflectivity}
          />
        )}
      </mesh>
    </group>
  )
})
Sprite3DMeshV2.displayName = 'Sprite3DMeshV2'

export default Sprite3DMeshV2
